<template>
  <div id="pricing" class="text-center price-plan">
    <div class="component-header">
      <h2 class="heading">
        <span class="text-blue">Affordable</span> for All Programs
      </h2>
      <p class="paragraph">
        We believe in simple, transparent pricing.
      </p>
    </div>
    <div class="q-px-lg">
      Pick the length you want students to have access to ChartFlow for
      <q-slider :min="6" :max="36" :step="6" markers :marker-labels="objMarkerLabel" v-model="plan_length"
        aria-label="Select subscription length in months" role="slider" :aria-valuemin="6" :aria-valuemax="36"
        :aria-valuenow="plan_length" :aria-valuetext="`${length_lookup[plan_length]} months selected`">
      </q-slider>
    </div>
    <div class="box">
      <!--<p class="plan-floating-text">MOST POPULAR</p>-->
      <div class="plan-price blue-text" aria-label="Current price per student">
        ${{ price_lookup(length_lookup[plan_length]) }} <span class="plan-title-sub">/ student</span>
      </div>
      <p class="plan-duration">{{ length_lookup[plan_length] }} months</p>
    </div>
    <div class="row">
      <div class="col box blue q-mx-md">
        <h4 class="text-bold text-primary">
          <q-icon name="school" aria-hidden="true"></q-icon>
          School Pay
        </h4>
        <q-input label="Number of Students" outlined v-model="students" type="number" min="1"
          aria-label="Enter number of students for pricing calculation" aria-describedby="student-count-help"></q-input>
        <div id="student-count-help" class="sr-only">Enter the number of students to calculate total pricing</div>
        <div class="price-label">Your Price:</div>
        <div class="plan-price blue-text">${{
          format_number_comma(price_lookup(length_lookup[plan_length]) * students) }}</div>
      </div>
      <h3 class="col" style="margin: auto">OR</h3>
      <div class="col box blue q-mx-md">
        <h4 class="text-bold text-primary">
          <q-icon name="credit_card" aria-hidden="true"></q-icon>
          Student Pay
        </h4>
        <div class="plan-price blue-text q-mb-lg">${{
          format_number_comma(price_lookup(length_lookup[plan_length])) }}<span class="plan-title-sub">/ student</span>
        </div>
        <p class="payment-info">Online Pay & Bookstore options are supported. <a class="text-secondary"
            href="mailto:sales@chartflow.io" target="_blank" rel="noopener noreferrer"
            aria-label="Contact Sales team via email">Contact Sales</a> for more
          information.</p>
      </div>
    </div>
    <div class="sub-header q-pa-md">
      <h3 class="heading">
        Ready to see a Live Demo? <a class="text-secondary" href="https://meetings.chartflow.io/zoom" target="_blank"
          rel="noopener noreferrer" aria-label="Schedule a live demo meeting (opens in new tab)"><br>Click Here to
          Schedule a Meeting</a>
      </h3>
      <p class="paragraph">
        Instructor Accounts are Free
      </p>
      <div class="button-container">
        <q-btn class="get-started-free text-white bg-secondary text-bold" label="Free Instructor Demo" to="/demo" />
      </div>
    </div>
  </div>
</template>

<script setup>
defineOptions({
  name: "PricePlan",
});

import { ref } from "vue";

const plan_length = ref(12);
const students = ref(1);
const objMarkerLabel = { 6: '3 Months', 12: '6 Months', 18: '12', 24: '18', 30: '24', 36: '36+ Months' }
const length_lookup = { 6: 3, 12: 6, 18: 12, 24: 18, 30: 24, 36: 36 }

function price_lookup(plan_length) {
  if (plan_length === 3) {
    return 15;
  }
  else if (plan_length === 6) {
    return 20;
  } else if (plan_length === 12) {
    return 30;
  } else if (plan_length === 18) {
    return 45;
  } else if (plan_length === 24) {
    return 60;
  } else if (plan_length === 36) {
    return 65;
  } else {
    return 0;
  }
}

function format_number_comma(value) {
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

</script>

<style scoped>
.price-plan {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.box {
  background-color: #ffffff;
  border-radius: 60px;
  padding: 1rem;
  min-width: 280px;
  /* Ensure a minimum size for small screens */
  max-width: 100%;
  /* Prevent overflowing */
  box-sizing: border-box;
}

/* Media queries for responsiveness */
@media (max-width: 1200px) {
  .box {
    flex-basis: 45%;
    /* Boxes will take 45% of the width on medium screens */
  }
}

@media (max-width: 768px) {
  .box {
    flex-basis: 100%;
    /* Full-width boxes on smaller screens */
    margin-bottom: 1rem;
    /* Add margin between stacked boxes */
  }
}

@media (max-width: 576px) {
  .box {
    padding: 1rem;
    /* Reduce padding on smaller screens */
  }
}

.plan-price {
  font-size: 64px;
  font-weight: 600;
  line-height: 77px;
}

.plan-title-sub {
  font-size: 16px;
  font-weight: 400;
  line-height: 19px;
}

.plan-duration {
  font-size: 24px;
  font-weight: 600;
  line-height: 29px;
}

.bullet-points p {
  font-size: 16px;
  font-weight: 500;
  line-height: 10px;
}

.blue {
  border: 1px solid #2f76d3;
}

.blue-text {
  color: #2f76d3;
}

h3 {
  margin: 0;
  padding: 20px;
  font-size: 24px;
  line-height: 36px;
}

.price-label {
  margin: 0;
  padding: 20px;
  font-size: 24px;
  line-height: 36px;
  font-weight: 600;
}

p {
  margin-top: 0;
  /* Remove default top margin */
  line-height: 26px;
  /* Adjust line height for readability */
  font-size: 16px;
}

.sub-header .heading {
  font-size: 36px;
  font-weight: 600;
  line-height: 43px;
}

.sub-header .paragraph {
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  color: #4a4a4a;
  /* Improved contrast from #a3a3a3 */
  padding-bottom: 20px;
}

.sub-header .footnote {
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  padding: 10px 0;
  color: #4a4a4a;
  /* Improved contrast from #a3a3a3 */
}

.get-started-free {
  background-color: #eaf1fb;
  color: #2f76d3;
  border-radius: 30px;
  padding: 10px 30px;
  font-size: 14px;
  margin: 0;
}

.payment-info {
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 2rem;
  letter-spacing: .0125em;
  margin: 10px 0;
  color: #4a4a4a;
}

.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}
</style>
